import { useState } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Check, X, Loader2 } from 'lucide-react';
import { RateCardPanel } from './RateCardPanel';

interface RateCardSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
  scrollToRoleId?: string;
}

export const RateCardSheet = ({ open, onOpenChange, projectId, scrollToRoleId }: RateCardSheetProps) => {
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveRef, setSaveRef] = useState<{ save: () => void; cancel: () => void } | null>(null);

  const handleSave = () => {
    if (saveRef) {
      setIsSaving(true);
      saveRef.save();
      setTimeout(() => setIsSaving(false), 300);
    }
  };

  const handleCancel = () => {
    if (saveRef) {
      saveRef.cancel();
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[60vw] p-0 flex flex-col h-full">
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <SheetTitle className="text-xl font-bold">Rate Card</SheetTitle>
          <SheetDescription>
            Manage project-level and person-level rate overrides
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto">
          <RateCardPanel
            projectId={projectId}
            scrollToRoleId={scrollToRoleId}
            onChangesChange={setHasChanges}
            onSaveRef={setSaveRef}
          />
        </div>

        <SheetFooter className="px-6 py-4 border-t flex-shrink-0 flex-row justify-between gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSaving}
          >
            Close
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={!hasChanges || isSaving}
            >
              <X className="mr-2 h-4 w-4" />
              Cancel Changes
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Save All Changes
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
